import { useEffect, useRef, useState } from 'react';
import longTermStrategyImage from '../../../assets/images/chienluocdaihandevsecops.jpg';

interface StrategyItem {
  id: string;
  icon: string;
  title: string;
  description: string;
  color?: string;
  iconColor?: string;
  iconBg?: string;
  image?: string;
  order?: number;
}

interface LongTermStrategySectionProps {
  items?: StrategyItem[];
  loading?: boolean;
}

export default function LongTermStrategySection({ items = [], loading = false }: LongTermStrategySectionProps) {
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.1 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  // Tách section image item và strategy items từ props
  const sectionImageItem = items.find((item: StrategyItem & { order?: number }) => 
    item.title === '_SECTION_IMAGE' || (item.order !== undefined && item.order < 0)
  );
  const strategyItems = items.filter((item: StrategyItem & { order?: number }) => 
    item.title !== '_SECTION_IMAGE' && (item.order === undefined || item.order >= 0)
  );

  // Lấy description và image từ section image item hoặc dùng giá trị mặc định
  const sectionDescription = sectionImageItem?.description || 'DevSecOps không chỉ giúp doanh nghiệp triển khai nhanh hơn, mà còn xây dựng nền tảng vận hành an toàn và bền vững. StackWay đồng hành cùng doanh nghiệp từ tư vấn, triển khai đến tối ưu liên tục trong suốt vòng đời hệ thống.';
  const sectionImage = sectionImageItem?.image || longTermStrategyImage;

  return (
    <section ref={sectionRef} className="pt-8 md:pt-12 lg:pt-20 pb-12 md:pb-20 lg:pb-32 bg-gradient-to-br from-[#1a3557] via-[#2a4b7c] to-[#1a3557] relative overflow-hidden">
      {/* Background decorative elements */}
      <div className="absolute inset-0 opacity-10">
        <div className="absolute top-1/4 left-0 w-[300px] h-[300px] md:w-[500px] md:h-[500px] bg-[#06b6d4] rounded-full blur-[120px]"></div>
        <div className="absolute bottom-1/4 right-0 w-[300px] h-[300px] md:w-[500px] md:h-[500px] bg-[#00b587] rounded-full blur-[120px]"></div>
      </div>

      {/* Grid pattern overlay */}
      <div className="absolute inset-0 opacity-5" style={{
        backgroundImage: 'linear-gradient(rgba(255,255,255,0.1) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.1) 1px, transparent 1px)',
        backgroundSize: '50px 50px'
      }}></div>

      <div className="relative max-w-7xl mx-auto px-4 md:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 md:gap-12 lg:gap-16 items-center">
          {/* Left Content */}
          <div className={`transition-all duration-1000 ${isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-10'}`}>
            {/* Badge */}
            <div className="inline-flex items-center justify-center mb-4 md:mb-6">
              <div className="px-4 md:px-5 py-2 md:py-2.5 bg-gradient-to-r from-[#06b6d4]/20 to-[#00b587]/20 rounded-full border border-[#06b6d4]/30">
                <span className="text-xs md:text-sm font-bold text-[#06b6d4] uppercase tracking-wider">
                  CHIẾN LƯỢC DÀI HẠN
                </span>
              </div>
            </div>

            {/* Heading */}
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white leading-tight mb-6 md:mb-8">
              DevSecOps là chiến lược dài hạn, không chỉ là dự án ngắn hạn
            </h2>

            {/* Description */}
            <div className="space-y-4 md:space-y-6 mb-8 md:mb-10">
              {sectionDescription.split('. ').filter(s => s.trim()).map((sentence, idx) => (
                <p key={idx} className="text-base md:text-lg text-white/90 leading-relaxed">
                  {sentence.trim()}{sentence.trim().endsWith('.') ? '' : '.'}
                </p>
              ))}
            </div>

            {/* Key Points */}
            <div className="space-y-3 md:space-y-4">
              {loading ? (
                <div className="text-center py-8">
                  <div className="inline-block w-12 h-12 border-4 border-white border-t-transparent rounded-full animate-spin"></div>
                  <p className="mt-4 text-white/80">Đang tải...</p>
                </div>
              ) : strategyItems.length === 0 ? (
                <div className="text-center py-8">
                  <p className="text-white/80">Chưa có nội dung</p>
                </div>
              ) : (
                <>
                  {strategyItems.map((item, index) => (
                <div 
                  key={index} 
                  className={`flex items-center gap-3 md:gap-4 group transition-all duration-1000 ${
                    isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-10'
                  }`}
                  style={{ transitionDelay: isVisible ? `${0.3 + index * 0.1}s` : '0s' }}
                >
                  <div className={`w-10 h-10 md:w-12 md:h-12 rounded-lg md:rounded-xl flex items-center justify-center border border-white/20 group-hover:scale-110 transition-transform duration-300 ${
                    item.iconBg 
                      ? (item.iconBg.includes('from-') || item.iconBg.includes('to-') 
                          ? `bg-gradient-to-br ${item.iconBg}` 
                          : item.iconBg)
                      : 'bg-white/10 backdrop-blur-sm'
                  }`}>
                    <i className={`${item.icon} text-lg md:text-xl ${item.iconColor || item.color || 'text-white'}`}></i>
                  </div>
                  <span className="text-white/90 font-medium text-base md:text-lg">{item.title || item.description}</span>
                </div>
                  ))}
                </>
              )}
            </div>
          </div>

          {/* Right Visual */}
          <div className={`relative transition-all duration-1000 delay-300 ${isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-10'}`}>
            {/* Main Card */}
            <div className="relative bg-white/5 backdrop-blur-xl rounded-2xl md:rounded-3xl p-6 md:p-8 border border-white/10 shadow-2xl">
              <div className="aspect-[4/3] rounded-xl md:rounded-2xl overflow-hidden bg-gradient-to-br from-[#2a4b7c] to-[#1a3557]">
                <img 
                  src={sectionImage}
                  alt="DevSecOps Long-term Strategy"
                  className="w-full h-full object-cover"
                />
              </div>

              {/* Floating Stats */}
              <div className={`absolute -top-3 -right-3 md:-top-6 md:-right-6 bg-gradient-to-br from-[#06b6d4] to-[#00b587] rounded-xl md:rounded-2xl p-4 md:p-6 shadow-2xl transition-all duration-1000 delay-500 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 -translate-y-4'}`}>
                <div className="flex items-center gap-2 md:gap-3">
                  <div className="w-10 h-10 md:w-12 md:h-12 bg-white/20 rounded-lg md:rounded-xl flex items-center justify-center">
                    <i className="ri-infinity-line text-xl md:text-2xl text-white"></i>
                  </div>
                  <div>
                    <div className="text-xl md:text-2xl font-bold text-white">24/7</div>
                    <div className="text-xs md:text-sm text-white/80 whitespace-nowrap">Đồng hành</div>
                  </div>
                </div>
              </div>

              <div className={`absolute -bottom-3 -left-3 md:-bottom-6 md:-left-6 bg-white backdrop-blur-xl rounded-xl md:rounded-2xl p-4 md:p-6 shadow-2xl border border-white/20 transition-all duration-1000 delay-700 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}>
                <div className="flex items-center gap-2 md:gap-3">
                  <div className="w-10 h-10 md:w-12 md:h-12 bg-gradient-to-br from-[#06b6d4]/20 to-[#00b587]/20 rounded-lg md:rounded-xl flex items-center justify-center">
                    <i className="ri-rocket-line text-xl md:text-2xl text-[#2a4b7c]"></i>
                  </div>
                  <div>
                    <div className="text-xl md:text-2xl font-bold text-[#2a4b7c]">100%</div>
                    <div className="text-xs md:text-sm text-[#2a4b7c]/70 whitespace-nowrap">Cam kết</div>
                  </div>
                </div>
              </div>
            </div>

            {/* Decorative circles */}
            <div className="absolute -z-10 top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[120%] h-[120%] bg-gradient-to-r from-[#06b6d4]/10 to-[#00b587]/10 rounded-full blur-3xl"></div>
          </div>
        </div>
      </div>
    </section>
  );
}
